import { useState, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Plus, Mail, Phone, MoreVertical, Eye, Edit, Power, Search, X } from 'lucide-react'
import { toast } from 'sonner'
import { clientsApi, businessesApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { BusinessResponse } from '@/types'
import { getCurrencySymbol } from '@/lib/utils'
import { usePermissions } from '@/contexts/PermissionContext'

const ALL_STATUSES = '__all__'

interface BusinessFilters {
  searchTerm: string
  status: string
}

export default function BusinessListPage() {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { canAddEdit } = usePermissions()
  const [viewingBusiness, setViewingBusiness] = useState<BusinessResponse | null>(null)

  // Separate state for input values vs applied filters
  const [searchInput, setSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES)
  const [appliedFilters, setAppliedFilters] = useState<BusinessFilters>({
    searchTerm: '',
    status: ALL_STATUSES,
  })

  const { data: client, isLoading: isLoadingClient } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => clientsApi.getById(clientId!),
    enabled: !!clientId,
  })

  // Filter businesses based on applied filters
  const filteredBusinesses = useMemo(() => {
    if (!client?.businesses) return []

    return client.businesses.filter(business => {
      // Search term filter (business name or email)
      if (appliedFilters.searchTerm) {
        const searchLower = appliedFilters.searchTerm.toLowerCase()
        const matchesName = business.business_name.toLowerCase().includes(searchLower)
        const matchesEmail = business.business_email.toLowerCase().includes(searchLower)
        if (!matchesName && !matchesEmail) return false
      }

      // Status filter
      if (appliedFilters.status !== ALL_STATUSES) {
        const isActive = appliedFilters.status === 'active'
        if (business.is_active !== isActive) return false
      }

      return true
    })
  }, [client?.businesses, appliedFilters])

  // Check if any filters are active
  const hasActiveFilters = appliedFilters.searchTerm || appliedFilters.status !== ALL_STATUSES

  const handleSearch = () => {
    setAppliedFilters({
      searchTerm: searchInput,
      status: statusFilter,
    })
  }

  const clearFilters = () => {
    setSearchInput('')
    setStatusFilter(ALL_STATUSES)
    setAppliedFilters({
      searchTerm: '',
      status: ALL_STATUSES,
    })
  }

  // Handle Enter key press in search input
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const toggleActiveStatusMutation = useMutation({
    mutationFn: ({ businessId, isActive }: { businessId: string; isActive: boolean }) =>
      businessesApi.update(businessId, { is_active: !isActive }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['client', clientId] })
      toast.success(`Business ${variables.isActive ? 'deactivated' : 'activated'} successfully`)
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update business status'
      toast.error(errorMessage)
    },
  })

  const handleToggleActive = (business: BusinessResponse) => {
    toggleActiveStatusMutation.mutate({ businessId: business.business_id, isActive: business.is_active })
  }

  if (isLoadingClient) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )
  }

  if (!client) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-destructive">Client not found</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/clients">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold">Businesses</h1>
            <p className="text-muted-foreground">
              Businesses for {client.client_name}
            </p>
          </div>
        </div>
        {canAddEdit('CLIENTS') && (
          <Link to={`/clients/${clientId}/businesses/new`}>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Business
            </Button>
          </Link>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Business List</CardTitle>
          <CardDescription>
            All businesses associated with {client.client_name}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          {client.businesses && client.businesses.length > 0 && (
            <div className="flex flex-wrap gap-3 items-center mb-4">
              <div className="relative flex-1 min-w-[200px] max-w-[300px]">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
                  <Search className="h-4 w-4 text-muted-foreground" />
                </div>
                <Input
                  placeholder="Search by name or email..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="pl-9"
                />
              </div>

              <Select
                value={statusFilter}
                onValueChange={(value) => setStatusFilter(value)}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_STATUSES}>All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>

              <Button onClick={handleSearch}>
                <Search className="mr-2 h-4 w-4" />
                Search
              </Button>

              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="mr-1 h-4 w-4" />
                  Reset
                </Button>
              )}
            </div>
          )}

          {filteredBusinesses.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Business Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Industry</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBusinesses.map((business) => (
                  <TableRow key={business.business_id}>
                    <TableCell className="font-medium">{business.business_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        {business.business_email}
                      </div>
                    </TableCell>
                    <TableCell>
                      {business.business_phone ? (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          {business.business_phone}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getCurrencySymbol(business.currency)}</span>
                        <span className="text-muted-foreground text-sm">{business.currency}</span>
                      </div>
                    </TableCell>
                    <TableCell>{business.industry || '-'}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          business.is_active
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                        }`}
                      >
                        {business.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/clients/${clientId}/businesses/${business.business_id}`)}>
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </DropdownMenuItem>
                          {canAddEdit('CLIENTS') && (
                            <DropdownMenuItem onClick={() => navigate(`/clients/${clientId}/businesses/${business.business_id}/edit`)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Edit Business
                            </DropdownMenuItem>
                          )}
                          {canAddEdit('CLIENTS') && (
                            <DropdownMenuItem onClick={() => handleToggleActive(business)}>
                              <Power className="mr-2 h-4 w-4" />
                              {business.is_active ? 'Mark as Inactive' : 'Mark as Active'}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : client.businesses && client.businesses.length > 0 ? (
            <div className="text-center py-12">
              <div className="text-muted-foreground mb-4">
                No businesses match your filters.
              </div>
              <Button variant="outline" onClick={clearFilters}>
                <X className="mr-2 h-4 w-4" />
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No businesses associated with this client yet.
            </div>
          )}

          {/* View Business Details Dialog */}
          <Dialog open={!!viewingBusiness} onOpenChange={(open) => !open && setViewingBusiness(null)}>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Business Details</DialogTitle>
                <DialogDescription>
                  Complete information for {viewingBusiness?.business_name}
                </DialogDescription>
              </DialogHeader>
              {viewingBusiness && (
                <div className="grid gap-6 py-4">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-lg border-b pb-2">Basic Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground text-sm">Business Name</Label>
                        <p className="font-medium">{viewingBusiness.business_name}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-sm">Email</Label>
                        <p className="font-medium">{viewingBusiness.business_email}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-sm">Phone</Label>
                        <p className="font-medium">{viewingBusiness.business_phone || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-sm">Currency</Label>
                        <p className="font-medium">{viewingBusiness.currency}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-sm">Industry</Label>
                        <p className="font-medium">{viewingBusiness.industry || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-sm">Website</Label>
                        <p className="font-medium">
                          {viewingBusiness.website ? (
                            <a href={viewingBusiness.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                              {viewingBusiness.website}
                            </a>
                          ) : '-'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Address Information */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-lg border-b pb-2">Address Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground text-sm">Address Line 1</Label>
                        <p className="font-medium">{viewingBusiness.business_address_line1 || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-sm">Address Line 2</Label>
                        <p className="font-medium">{viewingBusiness.business_address_line2 || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-sm">City</Label>
                        <p className="font-medium">{viewingBusiness.business_city || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-sm">State</Label>
                        <p className="font-medium">{viewingBusiness.business_state || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-sm">Postal Code</Label>
                        <p className="font-medium">{viewingBusiness.business_postal_code || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-sm">Country</Label>
                        <p className="font-medium">{viewingBusiness.business_country || '-'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Tax & Registration */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-lg border-b pb-2">Tax & Registration</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground text-sm">Tax ID</Label>
                        <p className="font-medium">{viewingBusiness.business_tax_id || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-sm">Registration Number</Label>
                        <p className="font-medium">{viewingBusiness.business_registration_no || '-'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Custom Fields */}
                  {viewingBusiness.custom_fields && Object.keys(viewingBusiness.custom_fields).length > 0 && (
                    <div className="space-y-4">
                      <h4 className="font-semibold text-lg border-b pb-2">Custom Fields</h4>
                      <div className="grid grid-cols-2 gap-4">
                        {Object.values(viewingBusiness.custom_fields).map((field) => (
                          <div key={field.config_id}>
                            <Label className="text-muted-foreground text-sm">{field.field_label}</Label>
                            <p className="font-medium">{field.field_value || '-'}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setViewingBusiness(null)}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

        </CardContent>
      </Card>
    </div>
  )
}

